/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CompassOutlined,
  FileOutlined,
  LoadingOutlined,
  NotificationOutlined,
  SettingOutlined,
  SolutionOutlined,
  HomeOutlined,
  AppstoreOutlined,
  CarOutlined,
  UserOutlined,
  TruckOutlined,
  IdcardOutlined,
  ReconciliationOutlined,
  OrderedListOutlined,
  SlidersOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import { Badge, notification } from 'antd'
import { ItemType, MenuItemType } from 'antd/es/menu/hooks/useItems'

import { useContractContracts, useFilters, useGeneral, useRoutesFilters, useUser } from '@contexts'
import { ROUTE_KEYS } from '@constants'
import { Filters, PATHS, RoutesFilters, StatusTicketEnum, WebTitleEnum } from '@types'
import { useToAuthorize } from '@hooks'
import { ImplementationTimeUtils } from '@utils'

import { useVehiclesContext } from '../../../contexts/VehiclesFiltersContext'
import { StyledMenu } from '../../../components/StyledComponents'
import { initialContractsFiltersState } from '../../../contexts/ContractsContext'
import {
  initialTrailersFiltersState,
  useTrailersContext,
} from '../../../contexts/TrailersFiltersContext'
import {
  initialCarriersFiltersState,
  useCarriersContext,
} from '../../../contexts/CarriersFiltersContext'
import { useFiltersV2 } from '../../../contexts/FiltersContextV2'

const AdminMenu: () => JSX.Element = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setFilters, resetFilters } = useFilters()
  const { state } = useUser()
  const gmtZone = state?.company?.gmtZone ?? 0
  const { setFilters: setContractFilters } = useContractContracts()
  const { setFilters: setOrdersFilters } = useFiltersV2()
  const { setFilters: setVehiclesFilters } = useVehiclesContext()
  const { setFilters: setTrailersFilters } = useTrailersContext()
  const { setFilters: setCarriersFilters } = useCarriersContext()
  const { setFilters: setRoutesFilters } = useRoutesFilters()
  const { data, isLoading } = useToAuthorize()
  const { setTicketToSearch } = useGeneral()
  const [notified, setNotified] = useState(false)
  const [requests, setRequests] = useState(0)

  useEffect(() => {
    document.title = `${!data ? WebTitleEnum.TITLE : `${`(${data})`} ${WebTitleEnum.TITLE}`}`
  }, [data])

  useEffect(() => {
    if (!notified) {
      if (data) {
        notification.info({
          message:
            // eslint-disable-next-line no-nested-ternary
            data > 0
              ? data === 1
                ? `${t('notifications.preffix')} una ${t(
                    'globals.generics.to_authorize_request',
                  ).toLowerCase()}`
                : `${t('notifications.preffix')} ${data} ${t(
                    'globals.generics.to_authorize_requests',
                  ).toLowerCase()}`
              : `${t('preffix_not')} ${t('globals.generics.to_authorize_requests').toLowerCase()}`,
          duration: 3,
        })
        setNotified(true)
        setRequests(data)
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (data && data > requests && data - requests > 1) {
        notification.info({
          message: `Tiene ${data} solicitudes para autorizar`,
          duration: 3,
        })
        setRequests(data)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const clearFiltersOnComponentChange = () => {
    setTicketToSearch(undefined)
    resetFilters()
  }
  const handleCleanFilters = () => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [],
      carrierMode: [],
      statusName: [],
      supplierId: [],
      destinationId: [],
      orderBy: undefined,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const clearContractFiltersOnComponentChange = () => {
    setContractFilters(initialContractsFiltersState.filters)
  }
  const clearOrdersFiltersOnComponentChange = () => {
    setOrdersFilters({ page: 1, limit: 10 })
  }
  const clearVehiclesFiltersOnComponentChange = () => {
    setVehiclesFilters({ page: 1, limit: 10 })
  }
  const clearTrailersFiltersOnComponentChange = () => {
    setTrailersFilters(initialTrailersFiltersState.filters)
  }
  const clearCarriersFiltersOnComponentChange = () => {
    setCarriersFilters(initialCarriersFiltersState.filters)
  }
  const clearSuppliersFiltersOnComponentChange = () => {
    setCarriersFilters(initialCarriersFiltersState.filters)
  }

  const onOngoingClick = () => {
    navigate(ROUTE_KEYS.ADMIN.HOME)
    handleCleanFilters()
    setFilters((prevState: Filters) => ({
      ...prevState,
      mondayOfWeek: ImplementationTimeUtils.companyStartOfIsoWeek(new Date(), gmtZone).toDate(),
    }))
    clearFiltersOnComponentChange()
  }

  const clearRoutesFiltersOnComponentChange = () => {
    setRoutesFilters((prevState: RoutesFilters) => ({
      ...prevState,
      page: 1,
      asnum: undefined,
      originCityCode: undefined,
      originPostCode: undefined,
      originZone: undefined,
      destinationCityCode: undefined,
      destinationPostCode: undefined,
      destinationZone: undefined,
      destinationRegion: undefined,
    }))
  }

  const onEVouchersClick = () => {
    navigate('/dashboard/admin/evouchers')
    setFilters((prevState: Filters) => ({
      ...prevState,
      statusName: [
        StatusTicketEnum.CONFIRMED,
        StatusTicketEnum.TRANSIT,
        StatusTicketEnum.DELIVERED,
      ],
    }))
    clearFiltersOnComponentChange()
  }
  const onDashboardWeeklyClick = () => {
    navigate(PATHS.ADMIN.DASHBOARD_WEEKLY)
  }
  const onDashboardMonthlyClick = () => {
    navigate(PATHS.ADMIN.DASHBOARD_MONTHLY)
  }
  const onRoutesClick = () => {
    navigate('/dashboard/admin/routes')
    clearRoutesFiltersOnComponentChange()
  }
  const onDriversClick = () => {
    navigate(PATHS.ADMIN.DRIVERS)
    clearVehiclesFiltersOnComponentChange()
  }
  const onVehiclesClick = () => {
    navigate(PATHS.ADMIN.VEHICLES)
    clearVehiclesFiltersOnComponentChange()
  }
  const onTrailersClick = () => {
    navigate(PATHS.ADMIN.TRAILERS)
    clearTrailersFiltersOnComponentChange()
  }
  const onCarriersClick = () => {
    navigate(PATHS.ADMIN.CARRIERS)
    clearCarriersFiltersOnComponentChange()
  }
  const onCarrierQuotasClick = () => {
    navigate(PATHS.ADMIN.CARRIER_QUOTAS)
    clearCarriersFiltersOnComponentChange()
  }
  const onContractsClick = () => {
    navigate(PATHS.ADMIN.CONTRACTS)
    clearContractFiltersOnComponentChange()
  }
  const onOrdersClick = () => {
    navigate(PATHS.ADMIN.ORDERS)
    clearOrdersFiltersOnComponentChange()
  }
  const onSuppliersClick = () => {
    navigate(PATHS.ADMIN.SUPPLIERS)
    clearSuppliersFiltersOnComponentChange()
  }
  const onCommercialUsersClick = () => {
    navigate(PATHS.ADMIN.COMMERCIAL)
    clearSuppliersFiltersOnComponentChange()
  }
  const onSettingsClick = () => {
    navigate('/dashboard/admin/settings')
    clearFiltersOnComponentChange()
  }
  const onVehicleSettingsClick = () => {
    navigate(PATHS.ADMIN.VEHICLE_SETTINGS)
    clearFiltersOnComponentChange()
  }
  const onSimClick = () => {
    navigate('/dashboard/admin/simulation')
    clearFiltersOnComponentChange()
  }
  const onOpenBatchesClick = () => {
    navigate('/dashboard/admin/open')
    clearFiltersOnComponentChange()
  }
  const onClosedBatchesClick = () => {
    navigate('/dashboard/admin/closed')
    clearFiltersOnComponentChange()
  }
  const onCatertifiedClick = () => {
    navigate('/dashboard/admin/certified')
    clearFiltersOnComponentChange()
  }
  const onContractsClickSupplier = () => {
    navigate(PATHS.ADMIN.CONTRACTS_SUPPLIERS)
    clearContractFiltersOnComponentChange()
  }

  const items: ItemType<MenuItemType>[] = useMemo(
    () => [
      {
        key: PATHS.ADMIN.ONGOING,
        onClick: onOngoingClick,
        icon: <HomeOutlined style={{ fontSize: '18px' }} />,
        label: (
          <>
            {t('admin_router.menu.requests')}
            {isLoading ? (
              <LoadingOutlined />
            ) : data && data > 0 ? (
              <Badge className="menu-badge" count={data}>
                <NotificationOutlined style={{ cursor: 'pointer' }} />
              </Badge>
            ) : (
              ''
            )}
          </>
        ),
      },
      {
        key: PATHS.ADMIN.EVOUCHERS,
        onClick: onEVouchersClick,
        icon: <FileOutlined style={{ fontSize: '17px' }} />,
        label: t('tpl_router.menu.evouchers'),
      },
      {
        key: 'submen1',
        icon: <AppstoreOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.batches'),
        children: [
          {
            key: PATHS.ADMIN.OPEN,
            onClick: onOpenBatchesClick,
            label: t('admin_router.menu.open'),
          },
          {
            key: PATHS.ADMIN.CLOSED,
            onClick: onClosedBatchesClick,
            label: t('admin_router.menu.closed'),
          },
          {
            key: PATHS.ADMIN.CERTIFIED,
            onClick: onCatertifiedClick,
            label: t('admin_router.menu.certified'),
          },
        ],
      },
      {
        key: PATHS.ADMIN.ROUTES,
        onClick: onRoutesClick,
        icon: <CompassOutlined style={{ fontSize: '19px' }} />,
        label: t('admin_router.menu.routes'),
      },
      {
        key: PATHS.ADMIN.CARRIER_QUOTAS,
        onClick: onCarrierQuotasClick,
        icon: <SlidersOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.cupos'),
      },
      {
        key: 'submen2',
        icon: <IdcardOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.carriers'),
        children: [
          {
            key: PATHS.ADMIN.CARRIERS,
            onClick: onCarriersClick,
            icon: (
              <span className="submenu-Ident">
                <IdcardOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.carriers'),
          },
          {
            key: PATHS.ADMIN.DRIVERS,
            onClick: onDriversClick,
            icon: (
              <span className="submenu-Ident">
                <UserOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.drivers'),
          },
          {
            key: PATHS.ADMIN.VEHICLES,
            onClick: onVehiclesClick,
            icon: (
              <span className="submenu-Ident">
                <CarOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.vehicles'),
          },
          {
            key: PATHS.ADMIN.TRAILERS,
            onClick: onTrailersClick,
            icon: (
              <span className="submenu-Ident">
                <TruckOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.trailers'),
          },
          {
            key: PATHS.ADMIN.CONTRACTS,
            onClick: onContractsClick,
            icon: (
              <span className="submenu-Ident">
                <SolutionOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: <span className="submenu">{t('admin_router.menu.contracts')}</span>,
          },
          {
            key: PATHS.ADMIN.ORDERS,
            onClick: onOrdersClick,
            icon: (
              <span className="submenu-Ident">
                <OrderedListOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: <span className="submenu">{t('admin_router.menu.orders')}</span>,
          },
        ],
      },
      {
        key: 'submen3',
        icon: <ReconciliationOutlined style={{ fontSize: '18px' }} />,
        label: t('tpl_router.menu.supplier'),
        children: [
          {
            key: PATHS.ADMINRO.SUPPLIERS,
            onClick: onSuppliersClick,
            icon: (
              <span className="submenu-Ident">
                <ReconciliationOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('tpl_router.menu.supplier'),
          },
          {
            key: PATHS.ADMIN.CONTRACTS_SUPPLIERS,
            onClick: onContractsClickSupplier,
            icon: (
              <span className="submenu-Ident">
                <SolutionOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: <span className="submenu">{t('admin_router.menu.contracts')}</span>,
          },
          {
            key: PATHS.ADMIN.COMMERCIAL,
            onClick: onCommercialUsersClick,
            icon: (
              <span className="submenu-Ident">
                <UserOutlined style={{ fontSize: '18px' }} />
              </span>
            ),
            label: t('admin_router.menu.commercial'),
          },
        ],
      },
      {
        key: 'submen4',
        icon: <SettingOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.settings'),
        children: [
          {
            key: PATHS.ADMIN.SETTINGS,
            onClick: onSettingsClick,
            label: t('admin_router.menu.general'),
          },
          {
            key: PATHS.ADMIN.VEHICLE_SETTINGS,
            onClick: onVehicleSettingsClick,
            label: t('admin_router.menu.vehicle_settings'),
          },
          {
            key: PATHS.ADMIN.SIMULATION,
            onClick: onSimClick,
            label: t('admin_router.menu.simulation'),
          },
        ],
      },
      {
        key: 'admin-dashboard-metrics',
        icon: <BarChartOutlined style={{ fontSize: '18px' }} />,
        label: t('admin_router.menu.dashboard'),
        children: [
          {
            key: PATHS.ADMIN.DASHBOARD_WEEKLY,
            onClick: onDashboardWeeklyClick,
            label: t('admin_router.menu.weekly'),
          },
          {
            key: PATHS.ADMIN.DASHBOARD_MONTHLY,
            onClick: onDashboardMonthlyClick,
            label: t('admin_router.menu.monthly'),
          },
        ],
      },
    ],
    [
      data,
      isLoading,
      onCatertifiedClick,
      onClosedBatchesClick,
      onContractsClick,
      onDashboardMonthlyClick,
      onDashboardWeeklyClick,
      onEVouchersClick,
      onOngoingClick,
      onOpenBatchesClick,
      onRoutesClick,
      onSettingsClick,
      onVehicleSettingsClick,
      onSimClick,
      onCarriersClick,
      onCarrierQuotasClick,
      onCommercialUsersClick,
      onDriversClick,
      onVehiclesClick,
      onTrailersClick,
      pathname,
      t,
    ],
  )

  return <StyledMenu selectedKeys={[pathname]} mode="inline" items={items} />
}

export { AdminMenu }
